import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { RouterLinkWithHref } from "@angular/router";
import {
  AccessLevel,
  AccountMarketplace,
  AccountState,
  AccountSubType,
  AccountType,
  Organization,
  Plan,
} from "@front/m19-api-client";
import { AccountMarketplaceEx, Marketplaces, OrganizationAccountGroups } from "@front/m19-models";
import {
  AccountMarketplaceService,
  AccountSelectionService,
  BillingService,
  isReadOnlyAccess,
} from "@front/m19-services";
import {
  ClipboardService,
  ConfirmPopupComponent,
  ConfirmPopupData,
  IBadgeComponent,
  IButtonDirective,
  ICheckboxComponent,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuSeparatorComponent,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ITableTdDirective,
  IToggleComponent,
  ITooltipDirective,
  MarketplaceFlagComponent,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountSettingsV2Service } from "@m19-board/services/account-settings.service";
import {
  UpdateCustomCampaignNameComponent,
  UpdateCustomCampaignNameModalData,
} from "@m19-board/settings/account-settings/account-group/custom-campaign-name-pop-up/update-custom-campaign-name.component";
import {
  ManageAuthorizedUsersModalComponent,
  ManageAuthorizedUsersModalData,
} from "@m19-board/settings/account-settings/account-group/manage-authorized-users-modal/manage-authorized-users-modal.component";
import {
  ModalUpdateMaxBidComponent,
  UpdateMaxBidData,
} from "@m19-board/settings/account-settings/account-group/max-bid-pop-up/update-max-bid.popup.component";
import {
  ConfirmDspAutomationModalComponent,
  ConfirmDspAutomationModalData,
} from "@m19-board/settings/account-settings/account-list/confirm-dsp-automation-modal.component";
import {
  ModalUpdateVendorMetricsComponent,
  ModalUpdateVendorMetricsData,
} from "@m19-board/settings/account-settings/custom-campaign-name-pop-up/vendor-metrics.popup";
import {
  LinkDspModalComponent,
  LinkDspModalData,
} from "@m19-board/settings/account-settings/link-dsp/link-dsp.component";
import {
  RenewAmazonAccessModalComponent,
  RenewAmazonAccessModalData,
} from "@m19-board/settings/account-settings/renew-amazon-access/renew-amazon-access-modal.component";
import { ShowDspComponent, ShowDspModalData } from "@m19-board/settings/account-settings/show-dsp.component";
import { AmazonInvoicesService } from "@m19-board/settings/amazon-invoices/amazon-invoices.service";
import { InputModalComponent, InputModalData } from "@m19-board/shared/input-modal/input-modal.component";
import { map, Observable, of, switchMap, take } from "rxjs";
import { AccountStatusBadgeComponent } from "../account-status-badge/account-status-badge.component";
import { ActivateDspAutomationCtaComponent } from "@m19-board/shared/activate-dsp-automation-cta/activate-dsp-automation-cta.component";
import { ActivateAdsAutomationCtaComponent } from "@m19-board/shared/activate-ads-automation-cta/activate-ads-automation-cta.component";

@Component({
  selector: "[app-account-list-row]",
  imports: [
    TranslocoDirective,
    ITableTdDirective,
    AccountStatusBadgeComponent,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    IButtonDirective,
    IToggleComponent,
    IDropdownMenuSeparatorComponent,
    ITooltipDirective,
    ICheckboxComponent,
    RouterLinkWithHref,
    IBadgeComponent,
    MarketplaceFlagComponent,
    IIconComponent,
    ActivateDspAutomationCtaComponent,
    ActivateAdsAutomationCtaComponent,
  ],
  templateUrl: "./account-list-row.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountListRowComponent {
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly clipboard = inject(ClipboardService);
  private readonly accountSettingsService = inject(AccountSettingsV2Service);
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly toasterService = inject(ToasterService);
  private readonly billingService = inject(BillingService);
  private readonly amazonInvoicesService = inject(AmazonInvoicesService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  readonly Marketplaces = Marketplaces;
  readonly AccountState = AccountState;

  public readonly accountMarketplace = input.required<AccountMarketplaceEx>();
  public readonly organization = input.required<OrganizationAccountGroups>();
  public readonly adminOrganizationIds = input<Set<number> | undefined>(undefined);
  public readonly isRowSelected = model<boolean>(false);

  private readonly selectedOrganizationAccountMarketplaces = toSignal(
    this.accountSelectionService.selectedOrganizationAccountMarketplaces$,
  );

  accountMarketplacesWithSameGlobalAdvertiser = computed<AccountMarketplace[] | undefined>(() => {
    const dspAdvertiser = this.accountMarketplace().dspAdvertiser;
    if (!dspAdvertiser) return undefined;

    if (!dspAdvertiser.isRegional) return [this.accountMarketplace()];

    return this.selectedOrganizationAccountMarketplaces()?.filter(
      (am) => am.dspAdvertiserId === dspAdvertiser.dspAdvertiserId,
    );
  });

  private readonly canLoginWithAmazonOrgIds = toSignal(this.accountSettingsService.canLoginWithAmazonOrgIds$);
  readonly canLoginWithAmazon = computed(
    () => !!this.canLoginWithAmazonOrgIds()?.has(this.accountMarketplace().resourceOrganizationId!),
  );

  readonly accountGroup = computed(() => {
    return this.organization().accountGroups.find((g) => g.id === this.accountMarketplace().accountGroupId)!;
  });

  readonly customCampaignNameAllowed = toSignal(this.accountSettingsService.customCampaignNameAllowed$);

  readonly isReadOnly = computed(() => {
    return isReadOnlyAccess(this.accountMarketplace().accessLevel);
  });

  readonly isAdmin = computed(() => {
    return this.accountMarketplace().accessLevel === AccessLevel.ADMIN;
  });

  readonly isAdminOrOwner = computed(() => {
    return this.adminOrganizationIds()?.has(this.accountMarketplace().resourceOrganizationId!) ?? false;
  });

  readonly isWrite = computed(() => {
    return this.accountMarketplace().accessLevel === AccessLevel.WRITE;
  });

  readonly isVendor = computed(() => {
    return this.accountMarketplace().accountType === AccountType.VENDOR;
  });

  readonly isBidderOn = computed(() => {
    return this.accountMarketplace().state === AccountState.BIDDER_ON;
  });

  readonly canUpdateMaxBid = computed(() => {
    return (this.isAdmin() || this.isWrite()) && this.isBidderOn();
  });

  readonly hasSeatOnM19 = computed(() => {
    return AccountMarketplaceService.m19SeatProfileIds.includes(
      this.accountMarketplace().dspAdvertiser?.profileId ?? 0,
    );
  });

  readonly marketplaceLabel = computed(() => {
    const marketplace = this.Marketplaces[this.accountMarketplace().marketplace];
    return marketplace.name;
  });

  readonly isDspBidderOn = computed(() => {
    const am = this.accountMarketplace();
    return (
      am.dspAdvertiser?.bidderOrganizationId !== undefined &&
      am.dspAdvertiser.bidderOrganizationId! == am.resourceOrganizationId
    );
  });

  dspBadgeLabel = computed(() => {
    return `DSP: ${this.accountMarketplace().dspAdvertiser?.name}`;
  });

  readonly hasActiveSubscription = computed(() => {
    return this.organization().hasActiveSubscription();
  });

  readonly accountBidderStatus = computed(() => {
    return this.accountSettingsService.getAccountBidderStatus(this.accountMarketplace());
  });

  readonly dspAdSpendFee = computed(() => {
    const plan = this.organization().organization.billingPlan;
    return this.hasSeatOnM19() ? (plan?.dspM19SeatAdSpendFee?.amount ?? 0) : (plan?.dspAdSpendFee?.amount ?? 0);
  });

  public readonly brandTrafficLoading = signal(false);
  private readonly organizationAmazonInvoices = toSignal(
    toObservable(this.organization).pipe(
      switchMap((org) => this.amazonInvoicesService.getOrganizationAmazonInvoices(org.organization.organizationId!)),
    ),
  );
  private readonly amazonInvoices = computed(() => {
    const orgAmazonInvoices = this.organizationAmazonInvoices();
    if (!orgAmazonInvoices) {
      return undefined;
    }
    const dpsAdvertiserId = this.accountMarketplace().dspAdvertiserId;
    if (!dpsAdvertiserId) {
      return [];
    }
    return orgAmazonInvoices.get(dpsAdvertiserId) ?? [];
  });
  public readonly overdueAmazonInvoices = computed(() =>
    this.amazonInvoices()?.filter((invoice) => AmazonInvoicesService.isOverdueInvoice(invoice)),
  );
  public readonly pendingAmazonInvoices = computed(() =>
    this.amazonInvoices()?.filter((invoice) => AmazonInvoicesService.isPendingInvoice(invoice)),
  );

  manageAuthorizedUsers(): void {
    const am = this.accountMarketplace();
    this.modalService.openModal<ManageAuthorizedUsersModalData, void>(ManageAuthorizedUsersModalComponent, {
      size: "xl",
      modalTitle: this.translocoService.translate("manage-authorized-users-modal.authorized_users", {
        name: am.accountName,
        marketplace: am.marketplace,
      }),
      data: {
        organizationId: am.resourceOrganizationId!,
        profileId: am.profileId!,
        accountName: am.accountName,
        marketplace: am.marketplace,
      },
    });
  }

  updateMaxBid(): void {
    const am = this.accountMarketplace();
    this.modalService.openModal<UpdateMaxBidData, void, ModalUpdateMaxBidComponent>(ModalUpdateMaxBidComponent, {
      size: "sm",
      modalTitle: `${this.translocoService.translate("update-max-bid.max_bid")} - ${am.accountName} - ${am.marketplace}`,
      data: {
        accountMarketplace: am,
      },
    });
  }

  copyAccountId(): void {
    const accountId = this.accountMarketplace().accountId;
    this.clipboard.copyToClipboard(accountId);
  }

  displayAmazonUserAccess(): void {
    const am = this.accountMarketplace();
    if (!am.amazonUserAccess) return;
    this.modalService.openModal<RenewAmazonAccessModalData, void>(RenewAmazonAccessModalComponent, {
      size: "lg",
      modalTitle: this.translocoService.translate("renew-amazon-access-modal.amazon_user_access_emails"),
      data: {
        amazonUserAccess: am.amazonUserAccess!,
        organizationId: am.resourceOrganizationId!,
        accountMarketplace: am,
        canRenewAccess: this.canLoginWithAmazon() && this.isAdminOrOwner() && !this.isReadOnly(),
      },
    });
  }

  updateCustomCampaignName(): void {
    const am = this.accountMarketplace();
    this.modalService.openModal<UpdateCustomCampaignNameModalData, void>(UpdateCustomCampaignNameComponent, {
      size: "lg",
      modalTitle: this.translocoService.translate("update-custom-campaign-name.custom_campaign_name", {
        accountName: am.accountName,
        marketplace: am.marketplace,
      }),
      data: {
        accountMarketplace: am,
      },
    });
  }

  updateCampaignLabel(): void {
    const am = this.accountMarketplace();
    const data: InputModalData = {
      title: this.translocoService.translate("account-setting.campaign_label"),
      initialValue: am.campaignLabel ?? "",
      maxLength: 10,
    };
    const ref = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: this.translocoService.translate("account-setting.edit_campaign_label"),
      data,
    });
    ref
      .afterClosed()
      .pipe(
        map((newName: string) => newName.trim()),
        switchMap((newName: string) => this.accountMarketplaceService.changeCampaignLabel(am, newName)),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("account-setting.campaign_label_updated"));
        },
        error: (error: string) => this.toasterService.error(error),
      });
  }

  updateVendorSourcingMetric(): void {
    const am = this.accountMarketplace();
    this.modalService.openModal<ModalUpdateVendorMetricsData, void>(ModalUpdateVendorMetricsComponent, {
      size: "lg",
      modalTitle: this.translocoService.translate("vendor-metrics-popup.vendor_metric_selection"),
      data: {
        accountMarketplace: am,
      },
    });
  }

  linkDspAdvertisingAccount(): void {
    const am = this.accountMarketplace();
    this.modalService.openModal<LinkDspModalData, void>(LinkDspModalComponent, {
      modalTitle: this.translocoService.translate("link-dsp.link_your_dsp_advertiser"),
      data: {
        accountMarketplace: am,
      },
    });
  }

  toggleBrandTraffic(): void {
    const am = this.accountMarketplace();

    this.brandTrafficLoading.set(true);
    this.accountMarketplaceService.updateBrandTrafficActivation(am, !am.brandedTrafficActivated).subscribe({
      next: () => {
        this.toasterService.success(
          this.translocoService.translate("brand-traffic.brand_traffic_activated_successfully"),
        );
        this.brandTrafficLoading.set(false);
      },
      error: (error) => {
        this.toasterService.error(
          this.translocoService.translate("brand-traffic.brand_traffic_activation_error"),
          error.message,
        );
        this.brandTrafficLoading.set(false);
      },
    });
  }

  displayDspAdvertisingAccountInformation(): void {
    const am = this.accountMarketplace();
    this.modalService.openModal<ShowDspModalData, void>(ShowDspComponent, {
      modalTitle: this.translocoService.translate("account-setting.dsp_advertiser_info"),
      data: {
        dspAdvertiserId: am.dspAdvertiserId!,
        accountMarketplace: am,
        disableLinkButton: this.isDspBidderOn(),
      },
    });
  }

  deleteAccount(): void {
    if (this.isBidderOn()) {
      return;
    }
    const am = this.accountMarketplace();
    const ref = this.modalService.openModal(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("account-setting.remove_accounts"),
      data: {
        message: this.translocoService.translate("account-setting.remove_accounts_message"),
        color: "red",
        confirmLabel: this.translocoService.translate("account-setting.remove_accounts_confirm_label"),
      },
    });

    ref
      .afterClosed()
      .pipe(switchMap(() => this.accountSettingsService.deleteSingleAccount(am)))
      .subscribe({
        next: () => {
          this.toasterService.success("Account(s) have been removed", "Account state updated");
        },
        error: (error: string) => {
          this.toasterService.error(error, "Account state update error");
        },
      });
  }

  grantSellingPartnerAccess(): void {
    const am = this.accountMarketplace();
    if (am.hasAccessToAdvertising) {
      const isKDP = am.accountSubType == AccountSubType.KDP_AUTHOR;
      this.accountSettingsService.grantSellingPartnerAccess(am.accountId, am.marketplace, am.accountType!, isKDP);
      return;
    }
    this.accountMarketplaceService
      .addAmazonUser(am.resourceOrganizationId!, am)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toasterService.success("Access successfully renewed", "Login with Amazon");
        },
        error: (errorMessage) => {
          this.toasterService.error(errorMessage, "Login with Amazon error");
        },
      });
  }

  stopDspBidder(): void {
    const am = this.accountMarketplace();

    this.confirmDspBidderAction(false)
      .pipe(
        switchMap(() => this.accountMarketplaceService.stopDspBidder(am.dspAdvertiser!, am.resourceOrganizationId!)),
      )
      .subscribe({
        next: () => {
          this.toasterService.success("DSP bidder successfully deactivated", "DSP bidder deactivation");
        },
        error: (error) => {
          this.toasterService.error(error, "DSP bidder deactivation error");
        },
      });
  }

  stopBidder(): void {
    const am = this.accountMarketplace();
    this.accountMarketplaceService
      .stopBidder(am)
      .pipe(switchMap((resources) => this.checkForSubscritionStop(resources)))
      .subscribe({
        next: (hasToStopSubscription) => {
          if (hasToStopSubscription) {
            this.toasterService.success("Subscription successfully stopped", "Subscription stop");
          } else {
            this.toasterService.success(am.accountName + " is now Active - Pull Stats", "Account state updated");
          }
        },
        error: (error) => this.toasterService.error(error, "Account state update error"),
      });
  }

  checkForSubscritionStop(resources: AccountMarketplaceEx[]): Observable<boolean> {
    const organizationId = this.organization().organization.organizationId;
    const lastAccountWithBidderOn = resources
      .filter((r) => r.resourceOrganizationId === organizationId)
      .every((r) => r.state !== AccountState.BIDDER_ON);
    if (!lastAccountWithBidderOn) return of(false);
    if (this.organization().organization.billingPlan?.plan !== Plan.PROFESSIONAL || !this.isAdmin()) return of(false);

    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("account-setting.no_more_accounts_with_ads_automation_enabled"),
      data: {
        message: this.translocoService.translate("account-setting.stop_your_professional_plan"),
      },
    });
    return ref.afterClosed().pipe(
      switchMap((confirm) =>
        confirm
          ? this.billingService.stopSubscription(this.organization().organization.organizationId!)
          : of(undefined),
      ),
      map((org: Organization[] | undefined) => org !== undefined),
    );
  }

  requestGrant() {
    const am = this.accountMarketplace();
    if (am.hasAccessToAdvertising) {
      const isKDP = am.accountSubType == AccountSubType.KDP_AUTHOR;
      this.accountSettingsService.grantSellingPartnerAccess(am.accountId, am.marketplace, am.accountType!, isKDP);
      return;
    }

    this.accountMarketplaceService
      .addAmazonUser(am.resourceOrganizationId!, am)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toasterService.success("Access successfully renewed", "Login with Amazon");
        },
        error: (errorMessage) => {
          this.toasterService.error(errorMessage, "Login with Amazon error");
        },
      });
  }

  editAccountGroupName(): void {
    const ref = this.modalService.openModal<InputModalData, string>(InputModalComponent, {
      modalTitle: "Change Account Group Name",
      data: {
        title: "Change Account Group Name",
        initialValue: this.accountMarketplace().accountGroupName,
        maxLength: 150,
      },
    });

    ref
      .afterClosed()
      .pipe(
        switchMap((newName: string) => {
          return this.accountMarketplaceService.renameAccountGroup(this.accountGroup(), newName);
        }),
      )
      .subscribe({
        next: () => {
          this.toasterService.success("Account group name updated");
        },
        error: (error: string) => {
          this.toasterService.error(error, "Account group name update error");
        },
      });
  }

  private confirmDspBidderAction(activatingBidder = true): Observable<boolean> {
    const am = this.accountMarketplace();
    const accountMarketplacesWithSameGlobalAdvertiser = this.accountMarketplacesWithSameGlobalAdvertiser();

    if (!accountMarketplacesWithSameGlobalAdvertiser || accountMarketplacesWithSameGlobalAdvertiser?.length <= 1)
      return of(true);

    return this.modalService
      .openModal<ConfirmDspAutomationModalData, boolean>(ConfirmDspAutomationModalComponent, {
        modalTitle: this.translocoService.translate("account-setting.dsp_activation_confirm_title"),
        data: {
          accountMarketplace: am,
          accountMarketplacesWithSameGlobalAdvertiser,
          adSpendFee: this.dspAdSpendFee(),
          onM19Seat: this.hasSeatOnM19(),
          activatingBidder,
        },
      })
      .afterClosed();
  }
}
